import { useCallback, useEffect, useState } from "react";
import { api, errMsg, num, rupee, pnlClass } from "@/lib/api";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const T = ({ k, v, cls = "text-slate-100", sub, id }) => (
  <div data-testid={id}><div className="eyebrow !text-[10px]">{k}</div><div className={`num text-base font-semibold ${cls}`}>{v}</div>{sub && <div className="num text-[10px] text-slate-500">{sub}</div>}</div>
);

export const WeeklyReport = () => {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback((week) => {
    setErr(null);
    api.get("/reports/weekly", { params: week ? { week } : {} }).then((r) => setRep(r.data)).catch((e) => setErr(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (err) return <div className="panel p-4 text-xs text-red-400" data-testid="weekly-report-error">{err}</div>;
  if (!rep) return null;
  const t = rep.totals;
  return (
    <div className="panel p-4" data-testid="panel-weekly-report">
      <div className="flex flex-wrap items-center gap-3">
        <span className="eyebrow">Weekly summary · {rep.week_start} → {rep.week_end}</span>
        <select data-testid="select-report-week" value={rep.week_start} onChange={(e) => load(e.target.value)} className="num ml-auto h-8 rounded-md border border-input bg-slate-900 px-2 text-xs">
          {rep.available_weeks.map((w) => <option key={w} value={w}>Week of {w}</option>)}
        </select>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-6">
        <T id="weekly-dry-pnl" k="Dry-run P&L" v={rupee(t.dry_pnl)} cls={pnlClass(t.dry_pnl)} sub={`${t.dry_trades} trades`} />
        <T id="weekly-bt-pnl" k="Backtest P&L" v={rupee(t.bt_pnl)} cls={pnlClass(t.bt_pnl)} sub={`${t.bt_trades} trades`} />
        <T id="weekly-drift" k="Drift (dry − backtest)" v={rupee(t.drift_dry_vs_bt)} cls={pnlClass(t.drift_dry_vs_bt)} sub={t.capture_pct === null ? "—" : `${num(t.capture_pct)}% captured`} />
        <T id="weekly-match" k="Signals caught" v={t.signal_match_pct === null ? "—" : `${num(t.signal_match_pct)}%`} cls={t.signal_match_pct !== null && t.signal_match_pct < 80 ? "down" : "text-slate-100"} sub={`${t.dry_matched}/${t.bt_signals} backtest signals`} />
        <T id="weekly-slip" k="Slippage cost" v={rupee(-t.slip_rupees)} cls={t.slip_rupees > 0 ? "down" : "text-slate-100"} />
        <T id="weekly-paper" k="Paper P&L" v={rupee(t.paper_pnl)} cls={pnlClass(t.paper_pnl)} sub={`${t.paper_trades} trades · 3y avg ${t.expected_3y === null ? "—" : rupee(t.expected_3y)}`} />
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="dense num w-full" data-testid="table-weekly-days">
          <thead><tr><th>Day</th><th>Backtest</th><th>Dry run</th><th>Caught</th><th>Slippage</th><th>Paper</th></tr></thead>
          <tbody>
            {rep.days.map((d, i) => (
              <tr key={d.date} data-testid={`weekly-day-${d.date}`} className={d.holiday !== null || d.future ? "opacity-50" : ""}>
                <td>{DOW[i]} {d.date.slice(5)}</td>
                {d.holiday !== null ? <td colSpan={5} className="!text-left text-slate-400">NSE holiday · {d.holiday}</td> : d.future ? <td colSpan={5} className="!text-left text-slate-500">upcoming</td> : (
                  <>
                    <td className={pnlClass(d.backtest.pnl)}>{d.backtest.trades} · {rupee(d.backtest.pnl)}</td>
                    <td className={pnlClass(d.dry.pnl)}>{d.dry.trades} · {rupee(d.dry.pnl)}{d.dry.aborted ? ` · ${d.dry.aborted} unfilled` : ""}</td>
                    <td className={d.bt_signals && d.dry_matched < d.bt_signals ? "down" : ""}>{d.bt_signals ? `${d.dry_matched}/${d.bt_signals}` : "—"}</td>
                    <td className={d.dry.slip_rupees > 0 ? "down" : ""}>{rupee(-d.dry.slip_rupees)}</td>
                    <td className={pnlClass(d.paper.pnl)}>{d.paper.trades} · {rupee(d.paper.pnl)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rep.backtest_error && <p className="mt-2 text-[11px] text-amber-400">{rep.backtest_error}</p>}
      <p className="mt-2 text-[11px] text-slate-500">Drift = what the dry run made minus what the backtest says it should have. Low "signals caught" means the engine missed trades (token, proxy or app offline); slippage shows fill quality.</p>
    </div>
  );
};
